import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment, WsmFeeBasis, WsmFeeScope } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { WsmAmount } from "@dashboard/wsmShared/money";
import { Box, Checkbox, Input, Select, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { getWsmComposeErrorMessage, getWsmComposeFieldError, getWsmComposeFormErrors } from "../../errors";
import { feeBasisLabels, feeScopeLabels } from "../../labels";
import { feeMessages, sectionMessages } from "../../messages";
import { WsmFeeFormData } from "../../types";
import { wsmFeeListPath } from "../../urls";
import { getFeeAmountUnit } from "../../utils";
import { WsmRail, WsmRailRow } from "../WsmRail/WsmRail";
import WsmYesNo from "../WsmYesNo/WsmYesNo";

export interface WsmFeeDetailsPageProps {
  title: string;
  productName?: string | null;
  productField?: React.ReactNode;
  currencyCode: string | null;
  initialData: WsmFeeFormData;
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmFeeFormData) => void;
  onDelete?: () => void;
}

export const WsmFeeDetailsPage = ({
  title,
  productName,
  productField,
  currencyCode,
  initialData,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onDelete,
}: WsmFeeDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmFeeFormData>(initialData);
  const change = (patch: Partial<WsmFeeFormData>) => setData(current => ({ ...current, ...patch }));
  const labelError = getWsmComposeFieldError(errors, "label");
  const skuError = getWsmComposeFieldError(errors, "sku");
  const amountError = getWsmComposeFieldError(errors, "amount");
  const formErrors = getWsmComposeFormErrors(errors, intl);
  const unit = getFeeAmountUnit(data.basis, currencyCode);
  const basis = data.basis === WsmFeeBasis.PERCENT ? "percent" : "fixed";

  return (
    <DetailPageLayout>
      <TopNav href={wsmFeeListPath} title={title} />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-fee-general">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(feeMessages.generalTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {formErrors.map(message => (
              <Text key={message} size={3} color="critical1" data-test-id="wsm-fee-error">
                {message}
              </Text>
            ))}
            {productField}
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              <Input
                label={intl.formatMessage(feeMessages.fieldLabel)}
                data-test-id="wsm-fee-label"
                disabled={disabled}
                value={data.label}
                onChange={event => change({ label: event.target.value })}
                error={!!labelError}
                helperText={
                  getWsmComposeErrorMessage(labelError, intl) ??
                  intl.formatMessage(feeMessages.fieldLabelHelp)
                }
              />
              <Input
                label={intl.formatMessage(feeMessages.fieldSku)}
                data-test-id="wsm-fee-sku"
                disabled={disabled}
                value={data.sku}
                onChange={event => change({ sku: event.target.value })}
                error={!!skuError}
                helperText={
                  getWsmComposeErrorMessage(skuError, intl) ??
                  intl.formatMessage(feeMessages.fieldSkuHelp)
                }
              />
            </Box>
            <FormSpacer />
            <Box display="grid" __gridTemplateColumns="1fr 1fr 1fr" gap={4}>
              <Select
                label={intl.formatMessage(feeMessages.fieldBasis)}
                data-test-id="wsm-fee-basis"
                disabled={disabled}
                options={Object.entries(feeBasisLabels).map(([value, message]) => ({
                  value,
                  label: intl.formatMessage(message),
                }))}
                value={data.basis}
                onChange={value => change({ basis: value as WsmFeeBasis })}
              />
              <Input
                // One column, two meanings: money under FIXED, a percentage
                // under PERCENT. The unit rides on the FIELD rather than in the
                // label, so the number is never read bare.
                label={intl.formatMessage(feeMessages.fieldAmount)}
                endAdornment={
                  unit ? (
                    <Text color="default2" data-test-id="wsm-fee-amount-unit">
                      {unit}
                    </Text>
                  ) : undefined
                }
                data-test-id="wsm-fee-amount"
                type="number"
                disabled={disabled}
                value={data.amount}
                onChange={event => change({ amount: event.target.value })}
                error={!!amountError}
                helperText={getWsmComposeErrorMessage(amountError, intl)}
              />
              <Select
                label={intl.formatMessage(feeMessages.fieldApplyTo)}
                data-test-id="wsm-fee-apply-to"
                disabled={disabled}
                options={Object.entries(feeScopeLabels).map(([value, message]) => ({
                  value,
                  label: intl.formatMessage(message),
                }))}
                value={data.applyTo}
                onChange={value => change({ applyTo: value as WsmFeeScope })}
              />
            </Box>
            <FormSpacer />
            <Checkbox
              data-test-id="wsm-fee-required"
              disabled={disabled}
              name="required"
              checked={data.required}
              onCheckedChange={checked => change({ required: !!checked })}
            >
              <Text size={3}>{intl.formatMessage(feeMessages.fieldRequired)}</Text>
            </Checkbox>
            <FormSpacer />
            <Input
              label={intl.formatMessage(feeMessages.fieldDeclineLabel)}
              data-test-id="wsm-fee-decline-label"
              // A required charge cannot be declined, so there is nothing to word.
              disabled={disabled || data.required}
              value={data.declineLabel}
              onChange={event => change({ declineLabel: event.target.value })}
              helperText={intl.formatMessage(feeMessages.fieldDeclineLabelHelp)}
            />
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <WsmRail
          title={intl.formatMessage(sectionMessages.railWhereItCharges)}
          testId="wsm-fee-rail"
        >
          <WsmRailRow label={intl.formatMessage(sectionMessages.product)}>
            {productName || intl.formatMessage(sectionMessages.railProductUnset)}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(feeMessages.fieldBasis)}>
            {intl.formatMessage(feeBasisLabels[data.basis])}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(feeMessages.colAmount)}>
            <WsmAmount amount={data.amount} currency={currencyCode} basis={basis} />
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(feeMessages.fieldApplyTo)}>
            {intl.formatMessage(feeScopeLabels[data.applyTo])}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(feeMessages.colRequired)}>
            <WsmYesNo value={data.required} />
          </WsmRailRow>
        </WsmRail>
      </DetailPageLayout.RightSidebar>

      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmFeeListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-fee-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmFeeDetailsPage;
